import React from 'react';
import { BarChart3Icon, ArrowUpIcon, ArrowDownIcon, EyeIcon, ClockIcon, MousePointerClickIcon } from 'lucide-react';
import { useClosingAssistant } from '../../contexts/ClosingAssistantContext';
export const ClosingReadinessPanel: React.FC = () => {
  const {
    customerProfile,
    closingReadiness
  } = useClosingAssistant();
  // Get color based on readiness
  const getReadinessColor = () => {
    switch (closingReadiness) {
      case 'high':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };
  // Get background color based on readiness
  const getReadinessBgColor = () => {
    switch (closingReadiness) {
      case 'high':
        return 'bg-green-100';
      case 'medium':
        return 'bg-yellow-100';
      case 'low':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };
  // Get readiness label
  const getReadinessLabel = () => {
    switch (closingReadiness) {
      case 'high':
        return 'High - Ready to Close';
      case 'medium':
        return 'Medium - Building Interest';
      case 'low':
        return 'Low - Information Gathering';
      default:
        return 'Unknown';
    }
  };
  // Calculate engagement metrics
  const sectionsViewed = customerProfile.viewedSections.length;
  const totalSections = 6; // Total number of sections in product details
  const engagementPercentage = Math.round(sectionsViewed / totalSections * 100);
  // Calculate total time spent
  const totalTimeSpent = customerProfile.interests.reduce((total, interest) => total + interest.timeSpent, 0);
  // Determine if readiness is trending up or down
  const readinessTrend = customerProfile.readinessScore > 50 ? 'up' : 'down';
  return <div className="p-4" data-id="element-784">
      <div className="flex items-center mb-4" data-id="element-785">
        <div className={`p-2 ${getReadinessBgColor()} rounded-full mr-3`} data-id="element-786">
          <BarChart3Icon className={`h-5 w-5 ${getReadinessColor()}`} data-id="element-787" />
        </div>
        <div data-id="element-788">
          <h3 className="font-medium text-gray-800" data-id="element-789">Closing Readiness</h3>
          <p className="text-xs text-gray-500" data-id="element-790">
            Real-time purchase intent analysis
          </p>
        </div>
      </div>
      {/* Readiness Score */}
      <div className="border border-gray-200 rounded-lg p-4 mb-4" data-id="element-791">
        <div className="flex justify-between items-center mb-2" data-id="element-792">
          <h4 className="text-sm font-medium text-gray-700" data-id="element-793">Readiness Score</h4>
          <div className="flex items-center" data-id="element-794">
            <span className={`text-lg font-bold ${getReadinessColor()}`} data-id="element-795">
              {customerProfile.readinessScore}%
            </span>
            {readinessTrend === 'up' ? <ArrowUpIcon className="h-4 w-4 text-green-500 ml-1" data-id="element-796" /> : <ArrowDownIcon className="h-4 w-4 text-red-500 ml-1" data-id="element-797" />}
          </div>
        </div>
        <div className="w-full h-3 bg-gray-200 rounded-full" data-id="element-798">
          <div className={`h-3 rounded-full ${customerProfile.readinessScore >= 70 ? 'bg-green-500' : customerProfile.readinessScore >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
          width: `${customerProfile.readinessScore}%`
        }} data-id="element-799"></div>
        </div>
        <p className="text-sm text-gray-600 mt-2" data-id="element-800">{getReadinessLabel()}</p>
      </div>
      {/* Engagement Metrics */}
      <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-801">
        <div className="border border-gray-200 rounded-lg p-3" data-id="element-802">
          <div className="flex items-center mb-1" data-id="element-803">
            <EyeIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-804" />
            <h4 className="text-xs font-medium text-gray-700" data-id="element-805">
              Content Viewed
            </h4>
          </div>
          <div className="flex items-center justify-between" data-id="element-806">
            <span className="text-lg font-bold text-gray-800" data-id="element-807">
              {engagementPercentage}%
            </span>
            <span className="text-xs text-gray-500" data-id="element-808">
              {sectionsViewed}/{totalSections} sections
            </span>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-3" data-id="element-809">
          <div className="flex items-center mb-1" data-id="element-810">
            <ClockIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-811" />
            <h4 className="text-xs font-medium text-gray-700" data-id="element-812">Time Engaged</h4>
          </div>
          <div className="flex items-center justify-between" data-id="element-813">
            <span className="text-lg font-bold text-gray-800" data-id="element-814">
              {totalTimeSpent}s
            </span>
            <span className="text-xs text-gray-500" data-id="element-815">total time</span>
          </div>
        </div>
      </div>
      {/* Readiness Factors */}
      <div className="border border-gray-200 rounded-lg p-3" data-id="element-816">
        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-817">
          Readiness Factors
        </h4>
        <div className="space-y-2" data-id="element-818">
          <div className="flex items-center justify-between" data-id="element-819">
            <span className="text-sm text-gray-600" data-id="element-820">Content Engagement</span>
            <div className="w-24 h-2 bg-gray-200 rounded-full" data-id="element-821">
              <div className="h-2 bg-blue-500 rounded-full" style={{
              width: `${engagementPercentage}%`
            }} data-id="element-822"></div>
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-823">
            <span className="text-sm text-gray-600" data-id="element-824">Pricing Interest</span>
            <div className="w-24 h-2 bg-gray-200 rounded-full" data-id="element-825">
              <div className="h-2 bg-blue-500 rounded-full" style={{
              width: `${customerProfile.interests.find(i => i.section === 'price')?.level || 0}%`
            }} data-id="element-826"></div>
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-827">
            <span className="text-sm text-gray-600" data-id="element-828">Booking Section Views</span>
            <div className="w-24 h-2 bg-gray-200 rounded-full" data-id="element-829">
              <div className="h-2 bg-blue-500 rounded-full" style={{
              width: `${customerProfile.viewedSections.includes('price') ? 100 : 0}%`
            }} data-id="element-830"></div>
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-831">
            <span className="text-sm text-gray-600" data-id="element-832">Click Activity</span>
            <div className="w-24 h-2 bg-gray-200 rounded-full" data-id="element-833">
              <div className="h-2 bg-blue-500 rounded-full" style={{
              width: '65%'
            }} data-id="element-834"></div>
            </div>
          </div>
        </div>
      </div>
      {/* Closing Recommendation */}
      <div className={`mt-4 p-3 rounded-lg ${closingReadiness === 'high' ? 'bg-green-50 border border-green-200' : closingReadiness === 'medium' ? 'bg-yellow-50 border border-yellow-200' : 'bg-blue-50 border border-blue-100'}`} data-id="element-835">
        <div className="flex items-center" data-id="element-836">
          <MousePointerClickIcon className={`h-4 w-4 mr-2 ${closingReadiness === 'high' ? 'text-green-500' : closingReadiness === 'medium' ? 'text-yellow-500' : 'text-blue-500'}`} data-id="element-837" />
          <h4 className="text-sm font-medium text-gray-800" data-id="element-838">
            Recommended Action
          </h4>
        </div>
        <p className="text-sm text-gray-600 mt-1" data-id="element-839">
          {closingReadiness === 'high' ? 'Customer appears ready to book. Consider offering a direct call-to-action or limited-time incentive to close.' : closingReadiness === 'medium' ? 'Customer is showing interest but may need more information. Focus on addressing potential objections and highlighting value.' : 'Customer is still in research phase. Continue providing information and building trust before attempting to close.'}
        </p>
      </div>
    </div>;
};